import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Matchday } from '@/api/types';
import styles from './ergebnistipp.module.css';

/**
 * Vor/Zurück-Navigation durch eine Liste von Spieltagen + Dropdown, um direkt
 * einen anzuspringen. `linkSuffix` hängt an die Ziel-URL an (z. B. "/overview").
 */
export function MatchdayNavigator({
  current,
  all,
  linkSuffix = '',
}: {
  current: Matchday;
  all: Matchday[];
  linkSuffix?: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const areaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent) {
      if (areaRef.current && !areaRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const idx = all.findIndex((m) => m.id === current.id);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;

  const goTo = (matchdayId: number) => {
    setOpen(false);
    navigate(`/matchdays/${matchdayId}${linkSuffix}`);
  };

  return (
    <div className={styles.mdNav} ref={areaRef}>
      <button
        type="button"
        className={styles.mdNavArrow}
        disabled={!prev}
        aria-label="Vorheriger Spieltag"
        onClick={() => prev && goTo(prev.id)}
      >
        ‹
      </button>
      <button
        type="button"
        className={styles.mdNavLabel}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {current.name}
      </button>
      <button
        type="button"
        className={styles.mdNavArrow}
        disabled={!next}
        aria-label="Nächster Spieltag"
        onClick={() => next && goTo(next.id)}
      >
        ›
      </button>

      {open && (
        <div className={styles.mdNavDropdown} role="menu">
          {all.map((m) => (
            <button
              key={m.id}
              type="button"
              role="menuitem"
              className={`${styles.mdNavItem} ${m.id === current.id ? styles.mdNavItemActive : ''}`}
              onClick={() => goTo(m.id)}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
